/*
  File:        api.ts
  Path:        src/utils/api.ts
  Project:     AQUORIX Pro Dashboard
  Version:     1.0.3
  Last Updated: 2026-02-17

  Description:
    Single source of truth for API base URL + helpers.
    - Supports Render/production via REACT_APP_API_BASE_URL
    - Keeps localhost fallback for local dev
    - Provides:
        apiUrl(path) -> string
        apiFetch(path, init, token?) -> Response
        getMe(token?) -> Response
        getMeJson<T>(token?) -> parsed JSON
        setActiveOperator(operatorId) -> Response

  IMPORTANT:
    - getMe() returns a Response (so callers can check res.ok / status)
    - getMeJson() returns parsed JSON for callers that just want data

  CHANGE LOG (append-only):
    - 2026-02-06 - v1.0.1
      - Change fallback base URL handling
    - 2026-02-16 - v1.0.2
      - Add API call helper for operator selection
    - 2026-02-17 - v1.0.3
      - FIX: Ensure JSON Content-Type for POST /api/v1/operator/active
      - FIX: Parse + validate operator_id as positive integer before sending
*/

import { supabase } from "../lib/supabaseClient";

// Normalize base URL (strip trailing slashes)
export const API_BASE_URL: string =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "") || "https://aquorix-api.onrender.com";

// Legacy compatibility (some older code does: import API_BASE_URL from "../utils/api")
export default API_BASE_URL;

export function apiUrl(path: string): string {
  if (!path) return API_BASE_URL;
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * Fetch helper that injects Bearer token.
 * If token not provided, it attempts to pull it from Supabase session.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  token?: string
): Promise<Response> {
  let accessToken = token;

  if (!accessToken) {
    const { data } = await supabase.auth.getSession();
    accessToken = data?.session?.access_token || undefined;
  }

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  // Do not force Content-Type for GET unless caller sets it.
  return fetch(apiUrl(path), {
    ...init,
    headers,
    cache: "no-store",
    credentials: "include",
  });
}

/**
 * /api/v1/me - returns Response
 */
export async function getMe(token?: string): Promise<Response> {
  return apiFetch("/api/v1/me", { method: "GET" }, token);
}

/**
 * /api/v1/me - returns parsed JSON
 */
export async function getMeJson<T = unknown>(token?: string): Promise<T> {
  const res = await getMe(token);
  if (!res.ok) throw new Error(`getMe HTTP ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Convert unknown operatorId input into a strict positive integer.
 * Accepts: number | string (e.g. "145")
 * Rejects: "", null, undefined, NaN, <= 0, non-numeric strings
 */
function toPositiveInt(value: unknown): number {
  if (typeof value === "number") {
    if (Number.isInteger(value) && value > 0) return value;
    return NaN;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return NaN;
    const n = parseInt(trimmed, 10);
    if (Number.isInteger(n) && n > 0) return n;
    return NaN;
  }

  return NaN;
}

/**
 * POST /api/v1/operator/active
 * Body: { operator_id }
 * Sets users.active_operator_id (server-authoritative operator context).
 */
export async function setActiveOperator(operatorId: string, token?: string): Promise<Response> {
  const operator_id = toPositiveInt(operatorId);

  if (!Number.isInteger(operator_id) || operator_id <= 0) {
    // Fail fast client-side with the same semantics as backend
    throw new Error("operator_id must be a positive number");
  }

  const body = JSON.stringify({ operator_id });

  return apiFetch(
    "/api/v1/operator/active",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    },
    token
  );
}
